export enum CrossCameraMode {
  Search = 'search',
  Auto = 'auto'
}

export type CrossCameraCamera = {
  camera_id: string
  camera_name: string
}

export type CrossCameraApp = {
  app_id: string
  app_name: string
  enabled: boolean
  mode: CrossCameraMode
  similarity_threshold: number
  candidate_retention_s: number
  rejoin_enabled: boolean
  rejoin_window_s: number
  rejoin_threshold: number
  cameras: CrossCameraCamera[]
}

export enum CrossCameraPipelineStatus {
  Pending = 1,
  Starting = 2,
  Running = 3,
  Degraded = 4,
  Stopped = 5,
  Failed = 6
}

export type CrossCameraPipeline = {
  id: string
  name: string
  application: { id: string; name: string }
  cameras: CrossCameraCamera[]
  device_id: string | null
  // The catalogue default when the pipeline names none.
  model: { id: string; name: string } | null
  mode: CrossCameraMode
  similarity_threshold: number
  candidate_retention_s: number
  rejoin_enabled: boolean
  rejoin_window_s: number
  rejoin_threshold: number
  faces_enabled: boolean
  search_min_similarity: number
  min_track_pictures: number
  enabled: boolean
  status: CrossCameraPipelineStatus
  status_display: string
  error_message?: string
  created_at: string
  updated_at: string
}

export type CrossCameraTrackingModel = {
  id: string
  name: string
  description: string
  embedding_size: number
  is_default: boolean
}

export type CrossCameraTracklet = {
  tracklet_id: string
  camera_id: string
  camera_name: string
  first_seen: string
  last_seen: string
}

export enum CrossCameraMatchQuality {
  Strong = 'strong',
  Good = 'good',
  Weak = 'weak',
  Face = 'face',
  ByHand = 'by_hand'
}

export type CrossCameraDeleteTarget =
  { identityId: string; appId: string } | { everyone: true }

// Served at pipelines/deleted-pipelines/identities/, beside each pipeline's own list.
export const DELETED_PIPELINES_ID = 'deleted-pipelines'

export type CrossCameraIdentity = {
  global_id: string
  app_id: string
  camera_count: number
  first_seen: string
  last_seen: string
  tracklets: CrossCameraTracklet[]
  crop_path: string | null
  match_score?: number | null
  match_quality?: CrossCameraMatchQuality | null
}

export type CrossCameraSighting = {
  tracklet_id: string
  camera_id?: string
  camera_name: string
  identity_id: string | null
  crop_path: string | null
  event_id: string | null
}

export type CrossCameraLookalike = CrossCameraSighting & {
  camera_id: string
  // Seconds since the epoch, as the device keeps time.
  first_seen: number
  last_seen: number
  score: number
  margin: number | null
}

export type CrossCameraLookalikeSearch = {
  source: CrossCameraSighting
  similarity_threshold: number
  results: CrossCameraLookalike[]
}
